import { useLayoutEffect, useRef } from 'react';
import type { Key } from '@react-types/shared';

type SelectionCollection = {
  size: number;
  getFirstKey: () => Key | null;
  getItem: (key: Key) => unknown;
  getKeyAfter: (key: Key) => Key | null;
};

type UseSyncedQuickSearchSelectionProps<TItems> = {
  collection: SelectionCollection;
  focusedKey?: string | null;
  isSectionedList: boolean;
  items: TItems;
  selectListItemFromListKey: (listKey?: Key | null) => void;
};

/**
 * Keeps the list selection aligned with the currently visible items after
 * tab switches and fresh search responses.
 */
export function useSyncedQuickSearchSelection<TItems>({
  collection,
  focusedKey,
  isSectionedList,
  items,
  selectListItemFromListKey,
}: UseSyncedQuickSearchSelectionProps<TItems>) {
  const previousItemsRef = useRef(items);

  useLayoutEffect(() => {
    const previousItems = previousItemsRef.current;
    previousItemsRef.current = items;

    if (previousItems === items && focusedKey != null) return;

    let nextSelectionKey: Key | null = null;

    if (collection.size > 0 && focusedKey) {
      const focusedItem = collection.getItem(focusedKey as Key);
      if (focusedItem) {
        nextSelectionKey = focusedKey as Key;
      }
    }

    if (!nextSelectionKey) {
      nextSelectionKey = collection.getFirstKey();

      if (nextSelectionKey && isSectionedList) {
        nextSelectionKey = collection.getKeyAfter(nextSelectionKey);
      }
    }

    if (nextSelectionKey) {
      selectListItemFromListKey(nextSelectionKey);
    }
  }, [
    collection,
    focusedKey,
    isSectionedList,
    items,
    selectListItemFromListKey,
  ]);
}
